import clsx from 'clsx';
import * as React from 'react';

import { FocusRingContext } from '../FocusRing';
import type { PolymorphicComponentProp, PolymorphicRef } from '../util';
import styles from './Pressable.module.css';

export type PressableProps<Tag extends React.ElementType> = PolymorphicComponentProp<Tag> & {
    /**
     * If true, the element is not pressable.
     */
    disabled?: boolean;

    /**
     * Callback when the element is pressed. It is roughtly equivalent to `onClick`.
     */
    onPress: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
};

/**
 * Interactive pressable view.
 */
export const Pressable = React.forwardRef(function PressableWithRef<Tag extends React.ElementType>(
    props: PressableProps<Tag>,
    ref: PolymorphicRef<Tag>
): React.ReactElement {
    const { as: As = 'div', style, onPress, disabled = false, ...rest } = props;
    const focusRingState = React.useContext(FocusRingContext);

    return (
        <As
            ref={ref}
            tabIndex={0}
            role="button"
            className={clsx('flex', style, {
                [styles.pressable]: !disabled,
                [styles.withFocusRing]: !disabled && focusRingState.enabled,
            })}
            {...rest}
            onClick={onPress}
        />
    );
});
